"use client";

import { useState } from "react";
import PhotoFrame from "./PhotoFrame";

export interface SpecTabRow {
  k: string;
  v: string;
}

export interface SpecActive {
  letter: string;
  name: string;
  pct: string;
  role: string;
  mech?: string;
  ref?: string;
}

export interface SpecHowToStep {
  n: string;
  when: string;
  label: string;
  body: string;
}

interface SpecTabsProps {
  overviewRows: SpecTabRow[];
  problemHeading?: string;
  problem?: string;
  approachHeading?: string;
  approach?: string;
  actives: SpecActive[];
  howTo: SpecHowToStep[];
  howToFinalNote?: { title: string; body: string };
  inci: string[];           // ordered list of INCI items
  inciFreeFrom?: string[];
  inciSummary?: string;
  applicationPhoto?: string;
  labels?: {
    technicalDossier?: string;
    notInThisFormula?: string;
  };
}

const TABS = [
  { id: "overview", label: "OVERVIEW" },
  { id: "actives", label: "ACTIVES" },
  { id: "howto", label: "HOW TO USE" },
  { id: "inci", label: "FULL INCI" },
] as const;

type TabId = typeof TABS[number]["id"];

export default function SpecTabs(props: SpecTabsProps) {
  const [tab, setTab] = useState<TabId>("actives");

  return (
    <section
      style={{
        padding: "96px var(--bp-edge)",
        borderBottom: "1px solid var(--bp-rule)",
        background: "var(--bp-bg-2)",
      }}
    >
      <div className="bp-mono"><span className="bp-bracket">{props.labels?.technicalDossier ?? "TECHNICAL DOSSIER"}</span></div>

      <div
        style={{
          marginTop: 24,
          display: "flex",
          gap: 4,
          flexWrap: "wrap",
          borderBottom: "1px solid var(--bp-rule)",
        }}
      >
        {TABS.map((t) => {
          const counts: Record<TabId, string> = {
            overview: "",
            actives: ` · ${String(props.actives.length).padStart(2, "0")}`,
            howto: "",
            inci: ` · ${String(props.inci.length).padStart(2, "0")}`,
          };
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                padding: "14px 20px 14px 0",
                marginRight: 24,
                marginBottom: -1,
                fontFamily: "var(--bp-mono)",
                fontSize: 11,
                letterSpacing: "0.14em",
                color: active ? "var(--bp-ink)" : "var(--bp-ink-3)",
                background: "none",
                borderTopWidth: 0,
                borderRightWidth: 0,
                borderLeftWidth: 0,
                borderBottomWidth: 1,
                borderBottomStyle: "solid",
                borderBottomColor: active ? "var(--bp-ink)" : "transparent",
                cursor: "pointer",
              }}
            >
              {t.label}{counts[t.id]}
            </button>
          );
        })}
      </div>

      <div style={{ marginTop: 40, minHeight: 360 }}>
        {tab === "overview" && (
          <Overview
            rows={props.overviewRows}
            problemHeading={props.problemHeading}
            problem={props.problem}
            approachHeading={props.approachHeading}
            approach={props.approach}
          />
        )}
        {tab === "actives" && <Actives actives={props.actives} />}
        {tab === "howto" && (
          <HowTo
            steps={props.howTo}
            applicationPhoto={props.applicationPhoto}
            finalNote={props.howToFinalNote}
          />
        )}
        {tab === "inci" && (
          <INCI
            inci={props.inci}
            freeFrom={props.inciFreeFrom}
            summary={props.inciSummary}
            notInThisFormulaLabel={props.labels?.notInThisFormula}
          />
        )}
      </div>
    </section>
  );
}

function Overview({
  rows,
  problemHeading,
  problem,
  approachHeading,
  approach,
}: {
  rows: SpecTabRow[];
  problemHeading?: string;
  problem?: string;
  approachHeading?: string;
  approach?: string;
}) {
  return (
    <div className="bp-spec-tab-grid">
      <div>
        {rows.map((r, i) => (
          <div
            key={i}
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1.3fr",
              gap: 16,
              padding: "14px 0",
              borderBottom: "1px solid var(--bp-rule)",
              fontSize: 14,
            }}
          >
            <span className="bp-mono-sm">{r.k.toUpperCase()}</span>
            <span style={{ color: "var(--bp-ink)" }}>{r.v}</span>
          </div>
        ))}
      </div>
      <div>
        {problem && (
          <>
            <h3
              style={{
                fontFamily: "var(--bp-serif)",
                fontWeight: 300,
                fontSize: 32,
                letterSpacing: "-0.02em",
                margin: 0,
              }}
            >
              <em style={{ fontStyle: "italic" }}>{problemHeading ?? "The problem."}</em>
            </h3>
            <p style={{ color: "var(--bp-ink-2)", fontSize: 15, lineHeight: 1.6, marginTop: 16 }}>
              {problem}
            </p>
          </>
        )}
        {approach && (
          <>
            <h3
              style={{
                fontFamily: "var(--bp-serif)",
                fontWeight: 300,
                fontSize: 32,
                letterSpacing: "-0.02em",
                margin: "40px 0 0",
              }}
            >
              <em style={{ fontStyle: "italic" }}>{approachHeading ?? "Our approach."}</em>
            </h3>
            <p style={{ color: "var(--bp-ink-2)", fontSize: 15, lineHeight: 1.6, marginTop: 16 }}>
              {approach}
            </p>
          </>
        )}
      </div>

      <style>{`
        .bp-spec-tab-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 64px;
        }
        @media (max-width: 900px) {
          .bp-spec-tab-grid { grid-template-columns: 1fr; gap: 40px; }
        }
      `}</style>
    </div>
  );
}

function Actives({ actives }: { actives: SpecActive[] }) {
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "48px 1.2fr 1fr 2fr auto",
          gap: 24,
          padding: "12px 0",
          borderBottom: "1px solid var(--bp-ink)",
        }}
        className="bp-actives-row"
      >
        {["", "INCI", "ROLE", "MECHANISM", "%"].map((h, i) => (
          <span key={i} className="bp-mono-sm" style={{ color: "var(--bp-ink)" }}>
            {h}
          </span>
        ))}
      </div>
      {actives.map((a) => (
        <div
          key={a.letter + a.name}
          className="bp-actives-row"
          style={{
            display: "grid",
            gridTemplateColumns: "48px 1.2fr 1fr 2fr auto",
            gap: 24,
            padding: "24px 0",
            borderBottom: "1px solid var(--bp-rule)",
            alignItems: "start",
          }}
        >
          <span className="bp-letter-badge">{a.letter}</span>
          <div>
            <div style={{ fontFamily: "var(--bp-serif)", fontSize: 20, fontWeight: 300, lineHeight: 1.2 }}>
              {a.name}
            </div>
            {a.ref && <div className="bp-mono-sm" style={{ marginTop: 6 }}>{a.ref}</div>}
          </div>
          <div style={{ fontSize: 13, color: "var(--bp-ink-2)" }}>{a.role}</div>
          <div style={{ fontSize: 14, color: "var(--bp-ink-2)", lineHeight: 1.5 }}>
            {a.mech ?? "—"}
          </div>
          <div style={{ fontFamily: "var(--bp-mono)", fontSize: 14 }}>{a.pct}</div>
        </div>
      ))}

      <style>{`
        @media (max-width: 720px) {
          .bp-actives-row {
            grid-template-columns: 36px 1fr auto !important;
            gap: 12px !important;
          }
          .bp-actives-row > :nth-child(3),
          .bp-actives-row > :nth-child(4) { display: none; }
        }
      `}</style>
    </div>
  );
}

function HowTo({
  steps,
  applicationPhoto,
  finalNote,
}: {
  steps: SpecHowToStep[];
  applicationPhoto?: string;
  finalNote?: { title: string; body: string };
}) {
  return (
    <div className="bp-spec-tab-grid">
      <div style={{ display: "flex", flexDirection: "column" }}>
        {steps.map((s, i) => (
          <div
            key={s.n}
            style={{
              display: "grid",
              gridTemplateColumns: "48px 80px 1fr",
              gap: 20,
              padding: "22px 0",
              borderTop: i === 0 ? "1px solid var(--bp-rule)" : "none",
              borderBottom: "1px solid var(--bp-rule)",
            }}
          >
            <span className="bp-mono">{s.n}</span>
            <span className="bp-mono-sm" style={{ color: "var(--bp-ink)" }}>
              {s.when}
            </span>
            <div>
              <div
                style={{
                  fontFamily: "var(--bp-serif)",
                  fontSize: 22,
                  fontWeight: 300,
                  letterSpacing: "-0.01em",
                }}
              >
                {s.label}
              </div>
              <p
                style={{
                  fontSize: 14,
                  color: "var(--bp-ink-2)",
                  lineHeight: 1.5,
                  margin: "8px 0 0",
                }}
              >
                {s.body}
              </p>
            </div>
          </div>
        ))}
      </div>
      <div>
        <PhotoFrame
          tone="mid"
          ratio="4/5"
          src={applicationPhoto}
          alt="Application"
          label="APPLICATION"
          corner="FIG. A"
        />
        {finalNote && (
          <div
            style={{
              marginTop: 16,
              padding: "14px 16px",
              border: "1px solid var(--bp-rule)",
              background: "var(--bp-bg)",
            }}
          >
            <span className="bp-mono-sm">{finalNote.title}</span>
            <p
              style={{
                margin: "8px 0 0",
                fontSize: 13,
                color: "var(--bp-ink-2)",
                lineHeight: 1.55,
              }}
            >
              {finalNote.body}
            </p>
          </div>
        )}
      </div>

      <style>{`
        .bp-spec-tab-grid {
          display: grid;
          grid-template-columns: 1.2fr 1fr;
          gap: 64px;
        }
        @media (max-width: 900px) {
          .bp-spec-tab-grid { grid-template-columns: 1fr; gap: 40px; }
        }
      `}</style>
    </div>
  );
}

function INCI({
  inci,
  freeFrom,
  summary,
  notInThisFormulaLabel,
}: {
  inci: string[];
  freeFrom?: string[];
  summary?: string;
  notInThisFormulaLabel?: string;
}) {
  return (
    <div className="bp-spec-tab-grid">
      <div>
        <p style={{ color: "var(--bp-ink-2)", fontSize: 14, margin: "0 0 20px" }}>
          {summary ??
            "Complete ingredient listing, ordered by concentration (INCI standard). Published in full — no proprietary blends."}
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "40px 1fr",
            borderTop: "1px solid var(--bp-rule)",
          }}
        >
          {inci.map((ing, i) => (
            <div key={i} style={{ display: "contents" }}>
              <div
                style={{
                  padding: "14px 0",
                  borderBottom: "1px solid var(--bp-rule)",
                  fontFamily: "var(--bp-mono)",
                  fontSize: 11,
                  color: "var(--bp-ink-3)",
                }}
              >
                {String(i + 1).padStart(2, "0")}
              </div>
              <div
                style={{
                  padding: "14px 0",
                  borderBottom: "1px solid var(--bp-rule)",
                  fontSize: 14,
                }}
              >
                {ing}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div>
        {freeFrom && freeFrom.length > 0 && (
          <div
            style={{
              padding: "14px 16px",
              background: "var(--bp-bg)",
              border: "1px solid var(--bp-rule)",
            }}
          >
            <span className="bp-mono-sm">{notInThisFormulaLabel ?? "NOT IN THIS FORMULA"}</span>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 12 }}>
              {freeFrom.map((x) => (
                <span
                  key={x}
                  style={{
                    fontFamily: "var(--bp-mono)",
                    fontSize: 10,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    padding: "6px 8px",
                    border: "1px solid var(--bp-rule)",
                    color: "var(--bp-ink-2)",
                  }}
                >
                  {x}
                </span>
              ))}
            </div>
          </div>
        )}
        <div
          style={{
            marginTop: 20,
            padding: "14px 16px",
            background: "var(--bp-bg)",
            border: "1px solid var(--bp-rule)",
          }}
        >
          <span className="bp-mono-sm">EU 1223/2009 · ANNEX I</span>
          <p
            style={{
              margin: "10px 0 0",
              fontSize: 13,
              color: "var(--bp-ink-2)",
              lineHeight: 1.55,
            }}
          >
            Full product information file on deposit with the responsible person in Freiburg, DE.
            Safety assessment conducted by a Qualified European Cosmetic Product Safety Assessor.
          </p>
        </div>
      </div>

      <style>{`
        .bp-spec-tab-grid {
          display: grid;
          grid-template-columns: 1.2fr 1fr;
          gap: 64px;
        }
        @media (max-width: 900px) {
          .bp-spec-tab-grid { grid-template-columns: 1fr; gap: 40px; }
        }
      `}</style>
    </div>
  );
}
